/* eslint-disable camelcase */
import {
  AGP_BACKUP_LOCATIONS,
  BACKUP_LOCATIONS, DATABASE_TYPES,
  DAYS, DEFAULT_HDD_SIZE, DEFAULT_SSD_SIZE, STORAGE_TYPES,
  SYNCHRONIZATION_MODES,
  VALIDATION,
} from 'constant';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { actions } from 'redux/actions';
import { ACTIONS_TYPES } from 'redux/helper';
import Shared from 'screens/shared';
import {
  _t, enumToArray, getKey, getSessionKey, isHddStorageType, isSsdStorageType, toInt,
} from 'utils';
import { getLocaleDateTime } from 'utils/time';
import { getMinDateTimeForBackup } from 'utils/database';
import { getResourceLimitsForContract } from 'utils/contract';

export default ({ onCancel }) => {
  const [maxValues, setMaxValues] = useState({
    maxCores: 1,
    maxRam: 2,
    maxStorage: 2,
  });
  const { maxCores, maxRam, maxStorage } = maxValues;

  const [model, setModel] = useState({
    postgresVersion: '',
    cores: maxCores === 0 ? 0 : 1,
    ram: maxRam === 0 ? 0 : 2,
    storageSize: Math.min(10, maxStorage),
    instances: 1,
    synchronizationMode: SYNCHRONIZATION_MODES.ASYNC,
    storageType: STORAGE_TYPES.HDD,
    backupLocation: 'eu-central-2',
  });

  const [changeFields, setChangeFields] = useState([]);

  const {
    [ACTIONS_TYPES.DATABASE.POSTGRESQL.GET_ALL_VERSIONS]: initVersions,
    [ACTIONS_TYPES.DATABASE.POSTGRESQL.GET_ALL_BACKUPS]: initBackups,
    [ACTIONS_TYPES.DATABASE.POSTGRESQL.GET_ALL]: clusters = [],
    [ACTIONS_TYPES.COMMON.BUFFER_CLONE_DATA]: cloneData,
    [ACTIONS_TYPES.CONTRACT.GET_ALL]: contracts = [],
    [ACTIONS_TYPES.DATA_CENTER.GET_ALL]: dataCenters = [],
    [ACTIONS_TYPES.DATABASE.GET_QUOTA]: dbQuota = {},
  } = useSelector((e) => e);

  let versions = initVersions || [];
  const backups = initBackups || [];

  const brandId = getSessionKey('brandId');

  const getHigherVersions = (version) => {
    const indexOfVersion = versions.findIndex((item) => item.name === version);
    let returnVersions = versions;
    if (indexOfVersion !== -1) {
      returnVersions = versions.slice(indexOfVersion, versions.length);
    }
    return returnVersions.map((item) => item.name);
  };

  if (cloneData) {
    versions = getHigherVersions(cloneData.postgresVersion);
  } else {
    versions = versions.map((item) => item.name);
  }

  const backupsByCluster = backups.reduce((acc, item) => {
    const cluster = clusters.find((clusterItem) => clusterItem.id === item.properties.clusterId);
    if (!cluster) return acc;
    acc[cluster.id] = [...(acc[cluster.id] || []), [item.id, item.properties.earliestRecoveryTargetTime || item.metadata.createdDate, item.properties.version]];
    return acc;
  }, {});

  const backupsSelectList = clusters.reduce((acc, item) => {
    const items = (backupsByCluster[item.id] || []).sort((a, b) => new Date(b[1]) - new Date(a[1]))
      .map(([id, date, version]) => ([id, `${getLocaleDateTime({ date })} - v${version}`]));
    acc[item.id] = { label: item.properties.displayName, items };
    return acc;
  }, {});

  const checkQuotas = (formValues) => {
    const values = formValues || model;
    const { instances } = values;
    const { quotaUsage = {}, quotaLimits = {} } = dbQuota || {};

    let maxCoresValue = toInt(quotaLimits?.cpu, 8) - toInt(quotaUsage?.cpu) - ((values.cores * instances) || 0);
    let maxRamValue = toInt(quotaLimits?.memory, 16) - toInt(quotaUsage?.memory) - ((values.ram * instances) || 0);
    const maxStorageValue = toInt(quotaLimits?.storage, 150) - toInt(quotaUsage?.storage) - ((values.storageSize * instances) || 0);

    const resourceLimits = getResourceLimitsForContract(contracts);

    const {
      coresPerContract = maxCoresValue,
      ramPerContract = (maxRamValue * 1024),
    } = resourceLimits;
    const maxContractRam = ramPerContract / 1024;
    if (coresPerContract < maxCoresValue) {
      maxCoresValue = coresPerContract;
    }
    if (maxContractRam < maxRamValue) {
      maxRamValue = maxContractRam;
    }

    const quotaValues = {
      maxCores: maxCoresValue,
      maxRam: maxRamValue,
      maxStorage: maxStorageValue,
    };

    setMaxValues({
      ...quotaValues,
    });

    return {
      ...quotaValues,
    };
  };

  const computeChangeFields = (values, quotaValues, oldModel) => {
    const { fromBackup } = values;

    let recoveryTargetTimeProps = { disabled: true };
    let backupIdHelpText;

    if (fromBackup) {
      const backup = backups.find((item) => item.id === fromBackup.backupId);
      const cluster = backup && clusters.find((item) => item.id === backup.properties.clusterId);

      const minDateTime = getMinDateTimeForBackup({ backup });

      backupIdHelpText = cluster && `Cluster: ${cluster.properties.displayName}`;
      recoveryTargetTimeProps = {
        disabled: !fromBackup.backupId,
        minDateTime,
        maxDateTime: new Date(),
      };
    }

    let quota = quotaValues || maxValues;

    const maxRamAndCurrentRam = quota.maxRam + values.ram;
    const maxCoresAndCurrentCores = quota.maxCores + values.cores;

    const ramOptions = [4, 8, 16].filter((e) => e < maxRamAndCurrentRam);
    const coreOptions = [4, 8].filter((e) => e < maxCoresAndCurrentCores);

    const maxStorageLimit = Math.floor((quota.maxStorage / values.instances) + values.storageSize);

    if (oldModel) {
      let localValues;
      if (isSsdStorageType(values.storageType) && isHddStorageType(oldModel.storageType)) {
        localValues = { ...values, storageSize: DEFAULT_SSD_SIZE };
        setModel(localValues);
      } else if (values.storageSize === DEFAULT_SSD_SIZE
        && isSsdStorageType(oldModel.storageType) && isHddStorageType(values.storageType)) {
        localValues = { ...values, storageSize: DEFAULT_HDD_SIZE };
        setModel(localValues);
      }

      if (localValues) {
        quota = checkQuotas(localValues);
      }
    }

    setChangeFields([
      { name: 'postgresVersion', props: { options: versions } },
      {
        name: 'storageSize',
        props: {
          element: <Shared.billing.DBaaSPriceEstimation model={values} databaseType={DATABASE_TYPES.POSTGRES} />,
          helpText: isSsdStorageType(values.storageType) ? _t('DATABASES.FORM_FIELDS.STORAGE_SIZE.SSD_INFO') : '',
        },
      },
      { name: 'backupId', props: { options: backupsSelectList, helpText: backupIdHelpText } },
      { name: 'recoveryTargetTime', props: recoveryTargetTimeProps },
      ...(
        quota.maxCores < 0
          ? [{ name: 'cores', props: { error: 'You already consumed the maximum limit', options: coreOptions } }]
          : [{
            name: 'cores',
            props: { error: '', range: [1, maxCoresAndCurrentCores > 8 ? maxCoresAndCurrentCores : 8], options: coreOptions },
          }]
      ),
      ...(
        quota.maxRam < 0
          ? [{ name: 'ram', props: { error: 'You already consumed the maximum limit', options: ramOptions } }]
          : [{
            name: 'ram',
            props: { error: '', range: [2, maxRamAndCurrentRam > 16 ? maxRamAndCurrentRam : 16], options: ramOptions },
          }]
      ),
      ...(
        quota.maxStorage < 0
          ? [{ name: 'storageSize', props: { error: `You already consumed the maximum limit. You have only ${maxStorageLimit} GB left.` } }]
          : [{ name: 'storageSize', props: { error: '' } }]
      ),
    ]);
  };

  const setCurrentModel = (formValues) => {
    if (formValues.fromBackup && !formValues.fromBackup.backupId) {
      delete formValues.fromBackup.recoveryTargetTime;
    }
    setModel(formValues);
  };

  const setModelData = () => {
    let localModel = model;

    if (cloneData) {
      const backup = backups && backups.find((item) => item.properties.clusterId === cloneData.id && item.properties.isActive);
      localModel = {
        ...cloneData,
        displayName: `${cloneData.displayName} - Copy`,
        ram: cloneData.ram / 1024,
        storageSize: cloneData.storageSize / 1024,
        connections: [],
        ...(backup ? { fromBackup: { backupId: backup.id, recoveryTargetTime: null } } : {}),
      };

      delete localModel.id;
    }
    setCurrentModel(localModel);
    return localModel;
  };

  useEffect(() => {
    if (initVersions && initBackups) {
      computeChangeFields(setModelData());
    }
    // eslint-disable-next-line
  }, [initVersions, initBackups]);

  useEffect(() => {
    checkQuotas();
    // eslint-disable-next-line
  }, [contracts.length]);

  useEffect(() => {
    actions.database.postgresql.getAllVersions();
    actions.database.postgresql.getAllBackups();
    actions.contract.getAll();
  }, []);

  const getDataCenterName = (dcId) => {
    const { properties = {} } = dataCenters.find(({ id }) => id === dcId) || {};
    return properties.name;
  };

  const fields = [
    {
      name: 'displayName',
      type: 'text',
      title: _t('DATABASES.FORM_FIELDS.DISPLAY_NAME.TITLE'),
    },
    {
      name: 'fromBackup',
      type: 'object',
      title: _t('DATABASES.FORM_FIELDS.FROM_BACKUP.TITLE'),
      subTitle: _t('DATABASES.FORM_FIELDS.FROM_BACKUP.SUBTITLE'),
      fields: [
        {
          name: 'backupId',
          type: 'dropdown',
          options: backupsSelectList,
          title: _t('DATABASES.FORM_FIELDS.BACKUP.TITLE'),
          clearable: true,
        },
        {
          name: 'recoveryTargetTime',
          type: 'mobile-date-time',
          title: _t('DATABASES.FORM_FIELDS.RECOVERY_TARGET_TIME.TITLE'),
          helpText: _t('DATABASES.FORM_FIELDS.RECOVERY_TARGET_TIME.HELP_TEXT'),
          clearable: true,
          clearSeconds: true,
        },
      ],
    },
    {
      name: 'location',
      type: 'location',
      title: _t('DATABASES.FORM_FIELDS.LOCATION.TITLE'),
    },
    {
      name: 'backupLocation',
      type: 'dropdown',
      title: _t('DATABASES.FORM_FIELDS.BACKUP_LOCATION.TITLE'),
      options: enumToArray(brandId === 'itzbund' ? AGP_BACKUP_LOCATIONS : BACKUP_LOCATIONS).map((e) => [e, e]),
    },
    {
      name: 'connections',
      type: 'array',
      icon: 'boxes',
      title: _t('DATABASES.FORM_FIELDS.CONNECTIONS.TITLE'),
      // subTitle: 'Multiple connections can be added', // currently only 1 connection supported
      actionButtonName: _t('DATABASES.FORM_FIELDS.CONNECTIONS.ACTION_BUTTON'),
      maxItems: 1,
      setItemTitle: (values) => getDataCenterName(values.datacenterId),
      setItemDetails: (values) => `LAN: ${values.lanId}${values.cidr ? `, IP: ${values.cidr}` : ''}`,
      updateProps: (props) => {
        const { location } = props;
        const _fields = fields.find((e) => e.name === 'connections').fields;
        _fields.find((e) => e.name === 'datacenterId').location = location;
        return { fields: _fields };
      },
      fields: [
        {
          name: 'datacenterId',
          type: 'datacenter',
          title: _t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.DATACENTER.TITLE'),
          setValueFrom: (e) => {
            actions.common.quickUpdate({ [ACTIONS_TYPES.DATA_CENTER.GET]: e });
            return e.id;
          },
        },
        {
          name: 'lanId',
          type: 'lan',
          hasNewOption: false,
          privateOnly: true,
          title: _t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.LAN_ID.TITLE'),
          updateProps: (values) => ({ disabled: !values.datacenterId }),
        },
        {
          name: 'cidr',
          type: 'text',
          title: _t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.PRIVATE_IP.TITLE'),
          helpText: (
            <span>
              {_t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.PRIVATE_IP.HELP_TEXT.TEXT')}
              &nbsp;
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://docs.ionos.com/cloud/managed-services/database-as-a-service/postgresql/dcd-howtos/setup-a-cluster-in-the-dcd/"
              >
                {_t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.PRIVATE_IP.HELP_TEXT.HREF')}
              </a>
            </span>
          ),
        },
      ],
      validations: [
        { name: 'datacenterId', type: VALIDATION.REQUIRED },
        { name: 'lanId', type: VALIDATION.REQUIRED },
        {
          name: 'lanId', type: VALIDATION.NUMBER, min: 1, max: 50,
        },
        { name: 'cidr', type: VALIDATION.REQUIRED },
        { name: 'cidr', type: VALIDATION.PRIVATE_IP_SUBNET },
      ],
    },
    {
      name: 'postgresVersion',
      type: 'dropdown',
      options: [],
      title: _t('DATABASES.FORM_FIELDS.POSTGRES_VERSION.TITLE'),
    },
    {
      name: 'instances',
      type: 'number',
      title: _t('DATABASES.FORM_FIELDS.INSTANCES.TITLE'),
      isNumber: true,
      min: 1,
      max: 10,
      helpText: _t('DATABASES.FORM_FIELDS.INSTANCES.HELP_TEXT'),
      onChange: () => setModel({ ...model }),
    },
    {
      name: 'synchronizationMode',
      type: 'dropdown',
      options: enumToArray(SYNCHRONIZATION_MODES).map((e) => [e, _t(`DATABASES.FORM_FIELDS.SYNCHRONIZATION_MODE.OPTIONS.${e}`)]),
      title: _t('DATABASES.FORM_FIELDS.SYNCHRONIZATION_MODE.TITLE'),
      helpText: _t('DATABASES.FORM_FIELDS.SYNCHRONIZATION_MODE.HELP_TEXT'),
      info: (
        <div>
          <strong>{_t('DATABASES.FORM_FIELDS.SYNCHRONIZATION_MODE.INFO.TITLE')}</strong>
          <ul>
            <li>
              <strong>{_t('DATABASES.FORM_FIELDS.SYNCHRONIZATION_MODE.INFO.LIST.ASYNCHRONOUS_ITEM.TITLE')}</strong>
              &nbsp;
              {_t('DATABASES.FORM_FIELDS.SYNCHRONIZATION_MODE.INFO.LIST.ASYNCHRONOUS_ITEM.CONTENT')}
            </li>
            <li>
              <strong>{_t('DATABASES.FORM_FIELDS.SYNCHRONIZATION_MODE.INFO.LIST.SYNCHRONOUS_ITEM.TITLE')}</strong>
              &nbsp;
              {_t('DATABASES.FORM_FIELDS.SYNCHRONIZATION_MODE.INFO.LIST.SYNCHRONOUS_ITEM.CONTENT')}
            </li>
            <li>
              <strong>{_t('DATABASES.FORM_FIELDS.SYNCHRONIZATION_MODE.INFO.LIST.STRICT_SYNCHRONOUS_ITEM.TITLE')}</strong>
              &nbsp;
              {_t('DATABASES.FORM_FIELDS.SYNCHRONIZATION_MODE.INFO.LIST.STRICT_SYNCHRONOUS_ITEM.CONTENT')}
            </li>
          </ul>
        </div>
      ),
    },
    {
      name: 'cores',
      type: 'slider',
      showValue: true,
      instantChange: false,
      title: _t('DATABASES.FORM_FIELDS.CORES.TITLE'),
      range: [1, maxCores > 8 ? maxCores : 8],
      options: [4, 8].filter((item) => item <= maxCores),
    },
    {
      name: 'ram',
      type: 'slider',
      showValue: true,
      instantChange: false,
      title: _t('DATABASES.FORM_FIELDS.RAM.TITLE'),
      range: [2, maxRam > 16 ? maxRam : 16],
      options: [4, 8, 16].filter((item) => item <= maxRam),
    },
    {
      name: 'storageType',
      type: 'dropdown',
      title: _t('DATABASES.FORM_FIELDS.STORAGE_TYPE.TITLE'),
      options: [STORAGE_TYPES.SSD_PREMIUM, STORAGE_TYPES.SSD_STANDARD, STORAGE_TYPES.HDD],
    },
    {
      name: 'storageSize',
      type: 'number',
      isNumber: true,
      title: _t('DATABASES.FORM_FIELDS.STORAGE_SIZE.TITLE'),
      step: '1',
      stepIsMandatory: true,
      instantChange: false,
      min: 0,
      suffix: 'GB',
      // max: Math.floor(maxStorage / model.instances + model.storageSize),
    },
    {
      name: 'credentials',
      type: 'object',
      title: _t('DATABASES.FORM_FIELDS.DATABASE_USER_CREDENTIALS.TITLE'),
      fields: [
        {
          name: 'username',
          type: 'text',
          title: _t('DATABASES.FORM_FIELDS.USERNAME.TITLE'),
          helpText: _t('DATABASES.FORM_FIELDS.USERNAME.HELP_TEXT'),
        },
        {
          name: 'password',
          type: 'password',
          title: _t('DATABASES.FORM_FIELDS.PASSWORD.TITLE'),
        },
      ],
      validations: [
        {
          name: 'username',
          type: VALIDATION.REQUIRED,
        },
        {
          name: 'username',
          type: VALIDATION.REGEX,
          regex: /^[0-9a-zA-Z]{3,256}$/,
          message: _t('COMMON.VALIDATION_TEXTS.LATIN_NUMBER_VALIDATION'),
        },
        // {
        //   name: 'password',
        //   type: VALIDATION.REGEX,
        //   regex: /^[0-9a-zA-Z]{8,50}$/,
        //   message: _t('COMMON.VALIDATION_TEXTS.PASSWORD_LATIN_NUMBER_VALIDATION'),
        // },
        {
          name: 'password',
          type: VALIDATION.REQUIRED,
        },
        {
          name: 'password',
          type: VALIDATION.STRING,
          min: 10,
          max: 50,
        },
      ],
    },
    {
      name: 'maintenanceWindow',
      type: 'object',
      title: _t('DATABASES.FORM_FIELDS.MAINTENANCE_WINDOW.TITLE'),
      subTitle: _t('DATABASES.FORM_FIELDS.MAINTENANCE_WINDOW.SUBTITLE'),
      fields: [
        {
          name: 'time',
          type: 'time',
          title: _t('Maintenance time'),
        },
        {
          name: 'dayOfTheWeek',
          type: 'dropdown',
          title: _t('Maintenance day'),
          options: DAYS,
        },
      ],
    },
  ];

  const validations = [
    { name: 'displayName', type: VALIDATION.REQUIRED },
    { name: 'displayName', type: VALIDATION.STRING, max: 256 },
    { name: 'location', type: VALIDATION.REQUIRED },
    { name: 'postgresVersion', type: VALIDATION.REQUIRED },
    { name: 'instances', type: VALIDATION.REQUIRED },
    {
      name: 'instances', type: VALIDATION.NUMBER, min: 1, max: 5,
    },
    {
      name: 'ram', type: VALIDATION.NUMBER, min: 2, max: 64,
    },
    {
      name: 'cores', type: VALIDATION.NUMBER, min: 1, max: 16,
    },
    {
      name: 'storageSize', type: VALIDATION.DECIMAL, min: 2, max: 2048,
    },
    { name: 'connections', type: VALIDATION.REQUIRED },
  ];

  const submitHandler = (values) => {
    values.ram *= 1024;
    values.storageSize *= 1024;

    if (values.fromBackup && !values.fromBackup.backupId) {
      delete values.fromBackup;
    }

    actions.database.postgresql.create({ properties: values }).then((cluster) => {
      if (cluster?.id && getKey('isAccessByDCD')) {
        window.top.postMessage({ event: 'onClusterCreate', id: cluster.id, database: 'postgresql' }, '*');
      }
    });
  };

  return (
    <Shared.forms.Form
      fields={fields}
      fieldsValues={model}
      onProps={model}
      validations={validations}
      onSubmit={submitHandler}
      changeFields={changeFields}
      onChange={(formValues) => {
        const oldModel = { ...model };
        setCurrentModel(formValues);
        computeChangeFields(formValues, checkQuotas(formValues), oldModel);
      }}
      onCancel={() => onCancel()}
      isCancel
      validateOnBlur
    >
    </Shared.forms.Form>
  );
};
